import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Eye, Loader2, CheckCircle, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getApiError } from '../../utils/errorHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * "View as Client" modal — admins preview the exact client UI for an order
 * without account-switching, and can upload documents on behalf of the client.
 *
 * The client UI is rendered in an isolated iframe at /orders/{id}?viewAsClient=true.
 * Read-only enforcement happens inside OrderDetailPage (pointer-events: none + isAdmin=false).
 *
 * Backend wiring:
 *   - POST /api/admin/orders/{id}/view-as-client-audit  (open event audit log)
 *   - POST /api/admin/orders/{id}/upload-on-behalf      (file upload + audit log)
 */
export default function ClientViewModal({ orderId, productName, documentRequirements = [], onClose, onUploaded }) {
  const [selectedRequirement, setSelectedRequirement] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fire the view-audit event once on open
  useEffect(() => {
    if (!orderId) return;
    const token = localStorage.getItem('token');
    fetch(`${API_URL}/api/admin/orders/${orderId}/view-as-client-audit`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    }).catch(() => { /* non-fatal; audit best-effort */ });
  }, [orderId]);

  // Esc key closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
  };

  const handleUpload = async () => {
    if (!selectedRequirement) {
      toast.error('Pick a document slot first');
      return;
    }
    if (!file) {
      toast.error('Select a file to upload');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('requirement_id', selectedRequirement);
      fd.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/orders/${orderId}/upload-on-behalf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success(`Uploaded "${file.name}" on behalf of client`);
      setFile(null);
      setSelectedRequirement('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      onUploaded?.();
    } catch (e) {
      toast.error(getApiError(e, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  // Only file-upload requirements belong in the "Document slot" dropdown.
  // Text/info-input fields (type === "text") must be excluded — they're not files.
  const fileUploadRequirements = (documentRequirements || []).filter(
    (r) => r && r.type === 'file_upload'
  );
  // When this app's frontend and backend are on the same origin (typical preview),
  // use a same-origin path so the iframe shares localStorage/cookies for auth.
  const sameOriginSrc = `/orders/${orderId}?viewAsClient=true`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="client-view-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700 bg-slate-900/80">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Eye className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">View as Client</h2>
              <p className="text-xs text-slate-400">
                Read-only preview of what the client sees{productName ? ` for "${productName}"` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors"
            data-testid="client-view-modal-close"
            title="Close (Esc)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — split: iframe + upload panel */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_360px] overflow-hidden">
          {/* LEFT: Client UI iframe (read-only) */}
          <div className="relative bg-slate-950 border-r border-slate-700 overflow-hidden">
            <div className="absolute top-2 left-2 z-10 px-2 py-1 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] uppercase tracking-wider rounded">
              Read-only preview
            </div>
            <iframe
              src={sameOriginSrc}
              title="Client view"
              className="w-full h-full bg-slate-950"
              style={{ border: 0 }}
              data-testid="client-view-iframe"
              sandbox="allow-same-origin allow-scripts"
            />
          </div>

          {/* RIGHT: Upload on behalf of client */}
          <div className="flex flex-col bg-slate-900 overflow-y-auto" data-testid="upload-on-behalf-panel">
            <div className="p-5 border-b border-slate-800">
              <div className="flex items-center gap-2 mb-1">
                <Upload className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-semibold text-white">Upload on behalf of client</h3>
              </div>
              <p className="text-xs text-slate-400">
                Files saved here count as the client's submission and satisfy the document requirement.
              </p>
            </div>

            <div className="p-5 space-y-4 flex-1">
              {/* Requirement select */}
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">Document slot</label>
                <select
                  value={selectedRequirement}
                  onChange={(e) => setSelectedRequirement(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500"
                  data-testid="upload-on-behalf-requirement-select"
                  disabled={uploading}
                >
                  <option value="">— Select a document slot —</option>
                  {fileUploadRequirements.length === 0 && (
                    <option value="" disabled>No file-upload slots available</option>
                  )}
                  {fileUploadRequirements.map((r) => (
                    <option key={r.id || r.document_type_id} value={r.id || r.document_type_id}>
                      {r.label || r.name || r.id || r.document_type_id}
                      {r.is_required === false ? '  (optional)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* File picker */}
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1.5">File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="block w-full text-xs text-slate-300 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-slate-800 file:text-slate-200 file:cursor-pointer hover:file:bg-slate-700"
                  data-testid="upload-on-behalf-file-input"
                  disabled={uploading}
                />
                {file && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-emerald-400">
                    <FileText className="w-3.5 h-3.5" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-slate-500">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={handleUpload}
                disabled={uploading || !selectedRequirement || !file}
                className="w-full px-4 py-2.5 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                data-testid="upload-on-behalf-submit-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Upload on behalf of client'}
              </button>

              {/* Info notes */}
              <div className="mt-4 space-y-2 text-[11px] text-slate-500">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-3 h-3 mt-0.5 text-emerald-500 flex-shrink-0" />
                  <span>Audited as <code className="text-slate-400">uploaded_by_admin_on_behalf</code> with your user id and timestamp.</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-3 h-3 mt-0.5 text-amber-500 flex-shrink-0" />
                  <span>The client account is never touched — no login switching occurs.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
